/**
 * Acompanhamento da emissão na TELA, compartilhado pela Venda Direta e pela Venda na Rota:
 *  - relê a situação no próprio sistema (nunca no provedor) em intervalos crescentes, sem
 *    limite enquanto a nota ainda vai mudar — o envio e as consultas são do worker;
 *  - deduz o estágio e as etapas reais;
 *  - prepara os arquivos e faz a entrega automática (DANFE na guia + um download);
 *  - mantém a guia de espera (preparada no clique) atualizada: etapas, tempo decorrido,
 *    rejeição com código e mensagem, falha de comunicação como "situação a confirmar". Quando
 *    o DANFE assume a guia, ela não é mais tocada.
 */
import type { FiscalDocumentDetail } from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "@/lib/api";
import {
  filesOf,
  renderTab,
  useAutoDelivery,
  usePreparedFiles,
  type DeliveryState,
} from "./emission-actions";
import {
  emissionStage,
  emissionTimeline,
  pollDelay,
  type EmissionStage,
  type TimelineStep,
} from "./emission-lib";
import { FISCAL_KEY } from "./lib";

/** Ainda sem os arquivos depois da autorização: continua relendo por até 5 minutos. */
const FILES_GRACE_MS = 5 * 60_000;

export type EmissionTracking = {
  doc: FiscalDocumentDetail | undefined;
  error: unknown;
  stage: EmissionStage | null;
  steps: TimelineStep[];
  delivery: DeliveryState;
};

export function useEmissionTracking(opts: {
  documentId: string;
  /** hora do "Finalizar" (ISO): base do tempo decorrido mostrado na guia */
  finishedAt: string;
  prefs: { openDanfe: boolean; download: boolean };
  pendingTab: Window | null;
}): EmissionTracking {
  const [started] = useState(() => Date.now());
  // relógio da tela: "worker parado" e o tempo decorrido dependem do tempo, não só de leituras
  const [now, setNow] = useState(() => Date.now());
  const tabOpen = opts.pendingTab !== null;
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), tabOpen ? 1_000 : 5_000);
    return () => clearInterval(id);
  }, [tabOpen]);

  const query = useQuery({
    queryKey: [...FISCAL_KEY, `/documentos/${opts.documentId}`],
    queryFn: () => apiFetch<FiscalDocumentDetail>(`/fiscal/documentos/${opts.documentId}`),
    refetchInterval: (q) => {
      const d = q.state.data;
      const elapsed = Date.now() - started;
      if (!d) return pollDelay(elapsed, true);
      const missingFiles =
        d.status === "autorizada" &&
        (filesOf(d).xml === undefined || filesOf(d).danfe === undefined);
      if (missingFiles && elapsed < FILES_GRACE_MS) return 5_000;
      return pollDelay(elapsed, emissionStage(d).waiting);
    },
    refetchIntervalInBackground: true,
  });
  const d = query.data;
  const stage = d ? emissionStage(d, now) : null;
  const prepared = usePreparedFiles(d);
  const filesReady = Boolean(prepared.data?.danfe);
  const model = d?.model === "65" ? "65" : "55";
  const timelineKey = stage?.key ?? null;
  const steps = useMemo(
    () =>
      timelineKey === null ? [] : emissionTimeline({ key: timelineKey }, { filesReady, model }),
    [timelineKey, filesReady, model],
  );
  const delivery = useAutoDelivery(d, opts.prefs, opts.pendingTab, prepared);

  // guia de espera: atualizada até o DANFE assumir (entrega feita) ou a guia ser fechada
  const handedOver = delivery.phase === "done" || delivery.phase === "error";
  const lastView = useRef("");
  const elapsedMs = now - Date.parse(opts.finishedAt);
  const tab = opts.pendingTab;
  const stageKey = stage?.key ?? "carregando";
  const stageTitle = stage?.title ?? "";
  const stageDetail = stage?.detail ?? "";
  const stageTone = stage?.tone ?? "info";
  const stageWaiting = stage?.waiting ?? true;
  const authorized = d?.status === "autorizada";
  useEffect(() => {
    if (!tab || tab.closed || handedOver) return;
    const view =
      stageKey === "carregando"
        ? {
            title: "Venda confirmada",
            detail: "Lendo a situação da nota no sistema…",
            busy: true,
          }
        : authorized
          ? {
              title: stageTitle,
              detail: "Preparando o DANFE para mostrar nesta guia…",
              busy: true,
              tone: "ok" as const,
            }
          : {
              title: stageTitle,
              detail: stageDetail,
              busy: stageWaiting && stageTone === "info",
              tone: stageTone,
            };
    const elapsed = Number.isFinite(elapsedMs) ? Math.max(0, elapsedMs) : undefined;
    const key = `${JSON.stringify(view)}|${JSON.stringify(steps)}|${Math.floor((elapsed ?? 0) / 1000)}`;
    if (key === lastView.current) return;
    lastView.current = key;
    renderTab(tab, {
      ...view,
      ...(steps.length > 0 ? { steps } : {}),
      ...(elapsed !== undefined ? { elapsedMs: elapsed } : {}),
    });
  }, [
    tab,
    handedOver,
    stageKey,
    stageTitle,
    stageDetail,
    stageTone,
    stageWaiting,
    authorized,
    steps,
    elapsedMs,
  ]);

  return { doc: d, error: query.error, stage, steps, delivery };
}
